<!--
  PhotoUploadField — a single-photo control supporting click-to-pick,
  drag & drop, and paste-from-clipboard. v-model is the stored S3 key
  (string | null). Presign+PUT is handled via the shared uploadLibraryPhoto.

  Paste works while `pasteActive` is true (bind it to the owning modal's
  open state) so multiple fields on a page don't fight over the clipboard.
-->
<template>
  <div class="photo-field" :class="{ 'drop-over': dragOver }"
       @dragover.prevent="dragOver = true"
       @dragleave.prevent="dragOver = false"
       @drop.prevent="onDrop">
    <div class="photo-thumb" :class="{ round }">
      <img v-if="url" :src="url" alt="photo" />
      <span v-else class="no-photo">—</span>
    </div>
    <label class="btn btn-outline btn-sm" :class="{ busy }">
      <input type="file" accept="image/*" hidden :disabled="busy" @change="onInput" />
      <span v-if="busy" class="spinner-sm"></span>
      <span v-else>{{ modelValue ? 'Replace' : 'Upload' }}</span>
    </label>
    <button v-if="modelValue" type="button" class="btn btn-outline btn-sm danger" @click="clear">Remove</button>
    <span class="photo-hint">Drop or paste an image here</span>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useAdminApi, photoUrl } from '@/composables/useAdminApi'
import { uploadLibraryPhoto } from '@/lib/uploads'

const props = withDefaults(defineProps<{
  modelValue: string | null
  kind: 'person' | 'location' | 'client'
  refId: number
  /** Only accept clipboard paste while this is true (e.g. modal open). */
  pasteActive?: boolean
  /** Round thumbnail (avatars) vs square. */
  round?: boolean
}>(), { pasteActive: true, round: false })

const emit = defineEmits<{ (e: 'update:modelValue', v: string | null): void }>()

const { apiFetch } = useAdminApi()
const busy = ref(false)
const dragOver = ref(false)
const url = computed(() => photoUrl(props.modelValue))

async function setFile(file: File | null | undefined) {
  if (!file || !file.type.startsWith('image/')) return
  busy.value = true
  try {
    const key = await uploadLibraryPhoto(apiFetch, file, props.kind, props.refId || 0)
    if (key) emit('update:modelValue', key)
  } finally { busy.value = false }
}
function onInput(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  setFile(file)
}
function onDrop(e: DragEvent) {
  dragOver.value = false
  setFile(e.dataTransfer?.files?.[0])
}
function onPaste(e: ClipboardEvent) {
  if (!props.pasteActive || busy.value) return
  const img = Array.from(e.clipboardData?.items || []).find(it => it.type.startsWith('image/'))
  if (!img) return
  const file = img.getAsFile()
  if (file) { e.preventDefault(); setFile(file) }
}
function clear() { emit('update:modelValue', null) }

onMounted(() => window.addEventListener('paste', onPaste))
onBeforeUnmount(() => window.removeEventListener('paste', onPaste))
</script>

<style scoped>
.photo-field { display: flex; align-items: center; gap: 0.5rem; }
.photo-field.drop-over { outline: 2px dashed var(--accent); outline-offset: 3px; border-radius: var(--radius-sm); }
.photo-thumb { width: 3rem; height: 3rem; border-radius: var(--radius-sm); overflow: hidden; background: var(--bg-elevated); display: flex; align-items: center; justify-content: center; flex: 0 0 auto; }
.photo-thumb.round { border-radius: 50%; }
.photo-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.photo-thumb .no-photo { color: var(--text-muted); }
.photo-hint { font-size: 0.72rem; color: var(--text-muted); margin-left: 0.1rem; }
.spinner-sm { width: 0.9rem; height: 0.9rem; border: 2px solid var(--text-muted); border-top-color: transparent; border-radius: 50%; display: inline-block; animation: pffspin 0.7s linear infinite; }
@keyframes pffspin { to { transform: rotate(360deg); } }
@media (max-width: 520px) { .photo-hint { display: none; } }
</style>
